import { Ispettorato } from "./ispettorato";
import { StatoIncarico } from "./stato-incarico";
import { Studio } from "./studio";
import { User } from "./user";
import * as moment from "moment";
import { Polizza } from "./polizza";
import { TipoDanno } from "./tipo-danno";
import { StatoVideoperizia } from "./stato-videoperizia";
import { StatoRiparazione } from "./stato-riparazione";
import { Riparazione } from "./riparazione";
import { ModuloContatto } from "./modulo-contatto";
import { PeriziaUniversale } from "./perizia-universale";

import { Controparte } from "./controparte";
import { AltroSoggetto } from "./altro-soggetto";
import { ModelWithAddress } from "./model-with-address";
import { Ramo } from "./ramo";
import { Tavola } from "./tavola";
import { Fabbricato } from "./fabbricato";
import { Annotazione } from "./annotazione";
import { File } from "./file";
import { WorkflowStep } from "./workflow-step";
import { AggiornamentoIncarico } from "./aggiornamento-incarico";
import { Compagnia } from "./compagnia";

/**
 * Rappresenta un incarico; estende "ModelWithAddress" perchè memorizziamo anche dove è avvenuto il danno.
 */
export class Incarico extends ModelWithAddress {
  static apiUrl: string = "incarichi";

  numero: string;

  data: moment.Moment;

  stato: StatoIncarico;

  numero_sinistro: string;
  data_sinistro: moment.Moment;
  data_denuncia: moment.Moment;
  broker: string;
  ramo: Ramo;
  tipo_di_danno: any;

  garanzia: any;
  ispettorato: Ispettorato;
  studio: Studio;
  studio_id: number;

  data_primo_contatto: moment.Moment;

  data_sopralluogo: moment.Moment;
  data_sopralluogo_fissata_data: moment.Moment | null;
  data_sopralluogo_fissata_ora: string | null;
  data_sopralluogo_fissata: moment.Moment | null;
  data_secondo_sopralluogo: moment.Moment;

  prima_interlocutoria: moment.Moment;
  seconda_interlocutoria: moment.Moment;
  terza_interlocutoria: moment.Moment;

  data_spedizione: moment.Moment;
  data_scadenza: moment.Moment;
  data_invio_atto: moment.Moment;
  date_invio_atto_count?: number;
  // Earliest ever recorded value from the historical log table
  data_invio_atto_first_by_created?: moment.Moment;

  data_sollecito_documentazione: moment.Moment;
  date_sollecito_documentazione_count?: number;
  data_sollecito_documentazione_first_by_created?: moment.Moment;

  pronta_liquidazione: boolean;
  over_10000: boolean;
  over_50000: boolean;
  riserva: boolean;
  foto_attestati_danno: boolean;
  documento_contabile_opere_ripristino: boolean;
  sollecitato: boolean;
  esclusione_interlocutoria: boolean;
  concordato: boolean;

  polizza: Polizza;
  from_compagnia_id?: number;
  from_compagnia?: Compagnia;
  tipo_danno: TipoDanno;

  riserva_di_danno: number;
  riserva_di_danno_range: string;
  indennizzo_risarcimento: number;

  inseritore: User;
  accertatore: User;
  accertatore_modified_at: moment.Moment | null;
  firmatario: User;
  incaricato: User;
  auditor: User;
  evasore: User;
  liquidatore: AltroSoggetto;
  contraente: any;

  note: string;
  note_riparazione: string;

  stato_riparazione: StatoRiparazione;
  stato_riparazione_id: number;
  stato_videoperizia: StatoVideoperizia;

  danno_accertato: number;
  importo_richiesto: number;
  supplementi_indennizzo: number;
  spese_demolizione_sgombero: number;

  parcella_onorario: number = 0;
  parcella_rimborso_spese_varie: number = 0;
  parcella_imponibile: number = 0;

  parcella_data_compilazione: moment.Moment;

  emolumento: number;
  emolumento_accertatore: number;
  emolumento_riparatore: number;
  emolumento_evasore: number;
  emolumento_auditor: number;
  emolumento_convalidato: boolean;
  emolumento_note: string;
  togli_importo: boolean;

  controparti: Controparte[];

  riparazioni: Riparazione[];
  annotazioni: Annotazione[];
  workflow_steps: WorkflowStep[];
  tavole: Tavola[];
  files: File[];

  spese_gestione_riparazione: number;
  percentuale_iva_riparazione: number;

  perizia_universale: PeriziaUniversale;
  modulo_contatto: ModuloContatto;
  modulo_sopralluogo: any;

  firmatario_id: number;
  auditor_id: number;
  evasore_id: number;
  incaricato_id: number;

  globale_fabbricati: boolean;

  doc_perizia: string;
  doc_perizia_0pe: any;
  // Created_at timestamp from incarico_step_workflow for current workflow context
  workflow_pivot_created_at?: string;
  // Workflow lightweight flags
  has_frontespizio?: boolean;
  frontespizio_file_id?: number | null;
  frontespizio_file_nome?: string | null;

  aggiornamenti_incarico: AggiornamentoIncarico[];

  deserialize(input: any): Incarico {
    let retV: Incarico = new Incarico();
    retV = super.deserialize(input) as Incarico;

    if (input) {
      if (input?.stato) {
        retV.stato = new StatoIncarico().deserialize(input.stato);
      }

      if (input?.studio) {
        retV.studio = new Studio().deserialize(input.studio);
      }

      if (input?.ispettorato) {
        retV.ispettorato = new Ispettorato().deserialize(input.ispettorato);
      }

      if (input?.tipo_danno) {
        retV.tipo_danno = new TipoDanno().deserialize(input.tipo_danno);
      }
      if (input?.ramo) {
        retV.ramo = new Ramo().deserialize(input.ramo);
      }

      if (input?.polizza) {
        retV.polizza = new Polizza().deserialize(input.polizza);
      }
      if (input?.from_compagnia) {
        retV.from_compagnia = new Compagnia().deserialize(input.from_compagnia);
      }
      if (input?.from_compagnia_id) {
        retV.from_compagnia_id = input.from_compagnia_id;
      }
      if (input?.stato_videoperizia) {
        retV.stato_videoperizia = new StatoVideoperizia().deserialize(
          input.stato_videoperizia
        );
      }
      if (input?.stato_riparazione) {
        retV.stato_riparazione = new StatoRiparazione().deserialize(
          input.stato_riparazione
        );
      }

      if (input?.tavole) {
        retV.tavole = input.tavole.map((r) => new Tavola().deserialize(r));
      }
      if (input?.files) {
        retV.files = input.files.map((r) => new File().deserialize(r));
      }
      if (input?.annotazioni) {
        retV.annotazioni = input.annotazioni.map((r) =>
          new Annotazione().deserialize(r)
        );
      }

      if (input?.riparazioni) {
        retV.riparazioni = input.riparazioni.map((r) =>
          new Riparazione().deserialize(r)
        );
      }
      if (input?.workflow_steps) {
        retV.workflow_steps = input.workflow_steps.map((r) =>
          new WorkflowStep().deserialize(r)
        );
      }
      if (input?.aggiornamenti_incarico) {
        retV.aggiornamenti_incarico = input.aggiornamenti_incarico.map((r) =>
          new AggiornamentoIncarico().deserialize(r)
        );
      }
      if (input?.modulo_contatto) {
        retV.modulo_contatto = new ModuloContatto().deserialize(
          input.modulo_contatto
        );
      }
      if (input?.modulo_sopralluogo) {
        retV.modulo_sopralluogo = input.modulo_sopralluogo;
      }
      if (input?.perizia_universale) {
        retV.perizia_universale = new PeriziaUniversale().deserialize(
          input.perizia_universale
        );
      }

      if (input?.date_invio_atto_count != null) {
        retV.date_invio_atto_count = Number(input.date_invio_atto_count) || 0;
      }

      if (input?.workflow_pivot_created_at) {
        retV.workflow_pivot_created_at = input.workflow_pivot_created_at;
      }
      if (Object.prototype.hasOwnProperty.call(input, "has_frontespizio")) {
        retV.has_frontespizio = !!input.has_frontespizio;
      }
      if (Object.prototype.hasOwnProperty.call(input, "frontespizio_file_id")) {
        retV.frontespizio_file_id =
          input.frontespizio_file_id != null
            ? Number(input.frontespizio_file_id)
            : null;
      }
      if (
        Object.prototype.hasOwnProperty.call(input, "frontespizio_file_nome")
      ) {
        retV.frontespizio_file_nome = input.frontespizio_file_nome ?? null;
      }

      if (input?.data) retV.data = moment(input.data);

      if (input?.data_spedizione)
        retV.data_spedizione = moment(input.data_spedizione);

      if (input?.data_primo_contatto)
        retV.data_primo_contatto = moment(input.data_primo_contatto);

      if (input?.data_sopralluogo)
        retV.data_sopralluogo = moment(input.data_sopralluogo);

      retV.data_sopralluogo_fissata_data = null;
      retV.data_sopralluogo_fissata_ora = null;
      retV.data_sopralluogo_fissata = null;

      if (input?.data_sopralluogo_fissata_data) {
        const parsedDate = moment(input.data_sopralluogo_fissata_data);
        if (parsedDate.isValid()) {
          retV.data_sopralluogo_fissata_data = parsedDate;
        }
      }

      if (typeof input?.data_sopralluogo_fissata_ora === "string") {
        const trimmed = input.data_sopralluogo_fissata_ora.trim();
        retV.data_sopralluogo_fissata_ora = trimmed !== "" ? trimmed : null;
      }

      if (input?.data_sopralluogo_fissata) {
        let parsed = moment(
          input.data_sopralluogo_fissata,
          moment.ISO_8601,
          true
        );

        if (!parsed.isValid()) {
          parsed = moment(
            input.data_sopralluogo_fissata,
            "YYYY-MM-DD HH:mm:ss",
            true
          );
        }

        if (!parsed.isValid()) {
          parsed = moment(input.data_sopralluogo_fissata);
        }

        if (parsed.isValid()) {
          retV.data_sopralluogo_fissata = parsed;
        }
      }

      if (
        !retV.data_sopralluogo_fissata &&
        retV.data_sopralluogo_fissata_data
      ) {
        retV.data_sopralluogo_fissata = retV.combineDateAndTime(
          retV.data_sopralluogo_fissata_data,
          retV.data_sopralluogo_fissata_ora
        );
      }

      if (input?.data_secondo_sopralluogo)
        retV.data_secondo_sopralluogo = moment(input.data_secondo_sopralluogo);

      if (input?.data_sinistro)
        retV.data_sinistro = moment(input.data_sinistro);

      if (input?.data_scadenza)
        retV.data_scadenza = moment(input.data_scadenza);

      // Data invio atto: respect explicit nulls from backend.
      // If the backend sends `data_invio_atto` explicitly (even if null), do NOT
      // fall back to historical latest; only use the latest value when the field
      // is entirely absent from the payload.
      if (Object.prototype.hasOwnProperty.call(input, "data_invio_atto")) {
        if (input.data_invio_atto) {
          retV.data_invio_atto = moment(input.data_invio_atto);
        }
        // else: keep undefined/null to represent absence
      } else if (input?.data_invio_atto_latest_by_created) {
        retV.data_invio_atto = moment(input.data_invio_atto_latest_by_created);
      }

      // Map earliest ever recorded "data invio atto" when provided by the backend
      if (input?.data_invio_atto_first_by_created) {
        retV.data_invio_atto_first_by_created = moment(
          input.data_invio_atto_first_by_created
        );
      }

      // Data sollecito documentazione: same pattern as data_invio_atto
      if (Object.prototype.hasOwnProperty.call(input, "data_sollecito_documentazione")) {
        if (input.data_sollecito_documentazione) {
          retV.data_sollecito_documentazione = moment(input.data_sollecito_documentazione);
        }
      } else if (input?.data_sollecito_documentazione_latest_by_created) {
        retV.data_sollecito_documentazione = moment(input.data_sollecito_documentazione_latest_by_created);
      }

      if (input?.data_sollecito_documentazione_first_by_created) {
        retV.data_sollecito_documentazione_first_by_created = moment(
          input.data_sollecito_documentazione_first_by_created
        );
      }

      if (
        Object.prototype.hasOwnProperty.call(input, "accertatore_modified_at")
      ) {
        retV.accertatore_modified_at = input.accertatore_modified_at
          ? moment(input.accertatore_modified_at)
          : null;
      }

      if (input?.data_denuncia)
        retV.data_denuncia = moment(input.data_denuncia);

      if (input?.parcella_data_compilazione)
        retV.parcella_data_compilazione = moment(
          input.parcella_data_compilazione
        );

      if (input?.prima_interlocutoria)
        retV.prima_interlocutoria = moment(input.prima_interlocutoria);

      if (input?.seconda_interlocutoria)
        retV.seconda_interlocutoria = moment(input.seconda_interlocutoria);

      if (input?.terza_interlocutoria)
        retV.terza_interlocutoria = moment(input.terza_interlocutoria);

      if (input?.liquidatore)
        retV.liquidatore = new AltroSoggetto().deserialize(input.liquidatore);

      for (let tipoCoinvolgimento of [
        "inseritore",
        "accertatore",
        "incaricato",
        "firmatario",
        "auditor",
        "evasore",
      ]) {
        if (input[tipoCoinvolgimento]) {
          retV[tipoCoinvolgimento] = new User().deserialize(
            input[tipoCoinvolgimento]
          );
        }
      }
    }

    return retV;
  }

  private combineDateAndTime(
    dateValue: moment.Moment | null,
    timeValue: string | null
  ): moment.Moment | null {
    if (!dateValue || !moment.isMoment(dateValue) || !dateValue.isValid()) {
      return null;
    }

    if (!timeValue || typeof timeValue !== "string" || !timeValue.trim()) {
      return dateValue.clone().startOf("day");
    }

    const normalizedRange = this.normalizeTimeRange(timeValue);
    if (!normalizedRange) {
      return dateValue.clone().startOf("day");
    }

    const start = normalizedRange.split(" - ")[0] ?? normalizedRange;
    const [hourStr, minuteStr] = start.split(":");
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);

    return dateValue.clone().hour(hour).minute(minute).second(0);
  }

  getDataSopralluogoFissataDisplay(
    format: string = "DD/MM/YYYY HH:mm"
  ): string {
    const value = this.data_sopralluogo_fissata;
    const timeDisplay = this.getTimeDisplayString();

    if (value && moment.isMoment(value) && value.isValid()) {
      const datePart = value.clone().format("DD/MM/YYYY");
      if (timeDisplay) {
        return `${datePart} ${timeDisplay}`;
      }
      return datePart;
    }

    if (value instanceof Date) {
      const datePart = moment(value).format("DD/MM/YYYY");
      if (timeDisplay) {
        return `${datePart} ${timeDisplay}`;
      }
      return datePart;
    }

    if (timeDisplay) {
      return timeDisplay;
    }

    if (typeof value === "string" && value) {
      let parsed = moment(value, moment.ISO_8601, true);
      if (!parsed.isValid()) {
        parsed = moment(value, "YYYY-MM-DD HH:mm:ss", true);
      }
      if (parsed.isValid()) {
        return parsed.format(format);
      }
      return value;
    }

    return "";
  }

  getDataSopralluogoFissataTimestamp(): number | null {
    let value = this.data_sopralluogo_fissata;

    if (
      (!value || !moment.isMoment(value) || !value.isValid()) &&
      this.data_sopralluogo_fissata_data
    ) {
      value = this.combineDateAndTime(
        this.data_sopralluogo_fissata_data,
        this.data_sopralluogo_fissata_ora
      );
    }

    if (value && moment.isMoment(value) && value.isValid()) {
      return value.valueOf();
    }

    if (value instanceof Date) {
      return value.getTime();
    }

    if (typeof value === "string" && value) {
      const parsed = moment(value);
      return parsed.isValid() ? parsed.valueOf() : null;
    }

    return null;
  }

  private getTimeDisplayString(): string | null {
    const raw =
      typeof this.data_sopralluogo_fissata_ora === "string"
        ? this.data_sopralluogo_fissata_ora.trim()
        : "";
    if (!raw) {
      return null;
    }

    const normalized = this.normalizeTimeRange(raw);
    return normalized ?? raw;
  }

  private normalizeTimeRange(value: string): string | null {
    if (!value) return null;

    let normalized = value.toLowerCase();
    normalized = normalized.replace(/[–—]/g, "-");
    normalized = normalized.replace(/\b(ore|ora|alle|h)\b/g, " ");
    normalized = normalized.replace(/\s+/g, " ");
    normalized = normalized.replace(/,/g, ".");
    normalized = normalized.trim();

    if (!normalized) return null;

    if (
      normalized.includes(" ") &&
      !normalized.includes("-") &&
      !normalized.includes("/")
    ) {
      const pieces = normalized.split(" ").filter((piece) => piece.length > 0);
      if (pieces.length > 1) {
        return null;
      }
    }

    const rawParts = normalized.split(/[-/]/);
    const parts = rawParts
      .map((part) => part.trim())
      .filter((part) => part.length > 0);

    const hasSeparator = normalized.includes("-") || normalized.includes("/");

    if (hasSeparator && parts.length !== rawParts.length) {
      return null;
    }

    if (parts.length === 0) {
      return null;
    }

    if (parts.length > 2) {
      return null;
    }

    const formatted: string[] = [];
    for (const part of parts) {
      const sanitized = this.sanitizeTimePart(part);
      if (!sanitized) {
        return null;
      }
      formatted.push(sanitized);
    }

    if (formatted.length === 2) {
      return `${formatted[0]} - ${formatted[1]}`;
    }

    return formatted[0];
  }

  private sanitizeTimePart(part: string | null): string | null {
    if (!part) return null;

    let clean = part.trim().toLowerCase();
    if (!clean) return null;

    clean = clean.replace(/[^0-9:.]/g, "");
    clean = clean.replace(/\./g, ":");

    if (/^\d{1,2}$/.test(clean)) {
      const hour = parseInt(clean, 10);
      if (Number.isNaN(hour) || hour < 0 || hour > 23) {
        return null;
      }
      return `${hour.toString().padStart(2, "0")}:00`;
    }

    if (/^\d{3,4}$/.test(clean)) {
      const raw = parseInt(clean, 10);
      if (Number.isNaN(raw)) {
        return null;
      }
      const hour = Math.floor(raw / 100);
      const minute = raw % 100;
      if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
        return null;
      }
      return `${hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;
    }

    if (/^\d{1,2}:\d{1,2}$/.test(clean)) {
      const [hourStr, minuteStr] = clean.split(":");
      const hour = parseInt(hourStr, 10);
      const minute = parseInt(minuteStr, 10);
      if (
        Number.isNaN(hour) ||
        Number.isNaN(minute) ||
        hour < 0 ||
        hour > 23 ||
        minute < 0 ||
        minute > 59
      ) {
        return null;
      }
      return `${hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;
    }

    if (/^\d{1,2}:\d{1,2}:\d{1,2}$/.test(clean)) {
      const [hourStr, minuteStr, secondStr] = clean.split(":");
      const hour = parseInt(hourStr, 10);
      const minute = parseInt(minuteStr, 10);
      const second = parseInt(secondStr, 10);
      if (
        Number.isNaN(hour) ||
        Number.isNaN(minute) ||
        Number.isNaN(second) ||
        hour < 0 ||
        hour > 23 ||
        minute < 0 ||
        minute > 59 ||
        second < 0 ||
        second > 59
      ) {
        return null;
      }
      return `${hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;
    }

    return null;
  }

  getHumanIdentifier(): string {
    let res: string = "" + this.numero;
    if (this.data) {
      res += "-";
      res += `${this.data.year()}`.substring(2, 4);
    }
    return res;
  }

  /**
   * Se l'incarico è chiuso, da la differenza tra data di spedizione e data dell'incarico,
   * altrimenti Restituisce il numero di giorni dall'attributo "data" (non la data di creazione nel nostro db, bensì la data dell'incarico secondo la compagnia)
   */
  get apertoDa() {
    if (this.data_spedizione) {
      return this.data_spedizione.diff(this.data, "days");
    }
    if (this.data) {
      return moment().diff(this.data, "days");
    }
    return 0;
  }

  get ultimaModificaInvioAtto() {
    if (this.data_invio_atto) {
      return moment().diff(this.data_invio_atto, "days");
    }
    return 0;
  }

  get ultimaModificaSollecitoDocumentazione() {
    if (this.data_sollecito_documentazione) {
      return moment().diff(this.data_sollecito_documentazione, "days");
    }
    return 0;
  }

  get emolumento_default() {
    /** dall'1 aprile, il compenso dell'eventuale accertatore deve essere tolto dall'incaricato  (indicazioni di gianluca) */
    // Controlla flag dello studio
    const detrazionePermessa = this.studio?.detrazione_accertamento_su_incaricato !== false;

    if (detrazionePermessa && this.data_sopralluogo >= moment("2022-04-01"))
      return (
        this.default_emolumento("incaricato") -
        (this.accertatore?.compenso_accertamenti ?? 0)
      );

    return this.default_emolumento("incaricato");
  }

  get emolumento_accertatore_default() {
    return this.default_emolumento("accertatore");
  }

  get emolumento_auditor_default() {
    return this.default_emolumento("auditor");
  }

  get emolumento_riparatore_default() {
    if (!this.stato_riparazione_id) return 0;
    return this.default_emolumento("incaricato");
  }

  get emolumento_evasore_default() {
    return this.default_emolumento("evasore");
  }

  private default_emolumento(
    userType: "incaricato" | "auditor" | "accertatore" | "evasore"
  ) {
    try {
      if (this.parcella_imponibile == 0)
        //caso speciale richiesto specificamente da Gianluca
        return 0;

      const user = this[userType] as User;
      let min = user.compenso_minimo;
      let max = user.compenso_massimo;
      let percentuale = user.compenso_percentuale;

      let compensoPercentuale = 0;

      if (percentuale)
        compensoPercentuale = this.parcella_imponibile * (percentuale / 100);

      let compenso = Math.max(min || 0, compensoPercentuale);
      compenso = Math.min(max || +Infinity, compenso);

      return compenso;
    } catch (e) {
      return 0;
    }
  }

  /** Alias for convenience to humanIdentifier */
  get riferimento_interno() {
    return this.humanIdentifier;
  }

  get anno() {
    return this.data?.year();
  }

  get amministratore() {
    if (this.polizza?.contraente instanceof Fabbricato)
      return this.polizza?.contraente?.amministratore;
    return this.polizza?.fabbricato?.amministratore;
  }

  static getDefaultSortKey() {
    return "humanIdentifier";
  }
  static getDefaultSortDirection() {
    return "desc";
  }
}
